import { MATH_FIELD_UI_SELECTORS } from '@magicschool/math';
import type { MathfieldElement } from 'mathlive';
import type { RefObject } from 'react';

const LISTEN_DELAY_MS = 100;

/**
 * Calls `onClickOutside` when the next mousedown lands outside the field. MathLive renders its
 * virtual keyboard and menus in detached containers, so those selectors count as inside; listening
 * starts late so the click that opened the field cannot immediately close it.
 */
export function listenForClickOutside(fieldRef: RefObject<MathfieldElement | null>, onClickOutside: () => void): () => void {
  const handleDocumentMouseDown = (event: MouseEvent) => {
    const target = event.target;
    const field = fieldRef.current;
    if (!(target instanceof Element) || !field) return;

    const isInField = field === target || field.contains(target) || (event.composedPath?.() ?? []).includes(field);
    const isFieldUi = MATH_FIELD_UI_SELECTORS.some((selector) => target.closest(selector));
    if (isInField || isFieldUi) return;

    onClickOutside();
  };

  const timeoutId = setTimeout(() => document.addEventListener('mousedown', handleDocumentMouseDown, true), LISTEN_DELAY_MS);

  return () => {
    clearTimeout(timeoutId);
    document.removeEventListener('mousedown', handleDocumentMouseDown, true);
  };
}
